import { ListApiResponse } from '../types/apiResponse';
import { ApproverNotificationUser } from '../types/approverNotificationUser';
import { AuditItem } from '../types/audit';
import { GameSkuBase } from '../types/games';
import {
  AddKeyWorkflowDto,
  KeyRequesterTag,
  KeyRequestMetricsAggregates,
  KeyWorkflow,
  KeyWorkflowItem,
  KeyWorkflowRequester,
  KeyWorkflowStep,
  KeyWorkflowStepApproverNotification,
} from '../types/keyWorkflows';
import { Stats } from '../types/stats';
import {
  apiUrl,
  getList,
  getSingleRecord,
  saveRecord,
  deleteRecord,
  apiOptions,
  handleResponse,
  handleError,
  getStatsData,
  getAll,
  postAction,
  get,
  putAction,
  getAuditData,
  exportData,
  deleteAction,
} from './apiUtils';
import type { ApiListParams, ApiExportParams } from '../types/api/listParams';
const baseUrl = apiUrl + '/workflows/';

export function getWorkflows(
  params?: ApiListParams
): Promise<ListApiResponse<KeyWorkflowItem>> {
  return getList(`${baseUrl}search`, params);
}
export function getAllWorkflows(): Promise<KeyWorkflowItem[]> {
  return getAll(baseUrl);
}
export function getRequestableKeyWorkflows(
  companyId: string,
  gameId: string,
  skuId: string
): Promise<KeyWorkflowItem[]> {
  return get(
    `${baseUrl}requestable/${companyId || ''}?gameId=${gameId || ''}&skuId=${
      skuId || ''
    }`
  );
}

export function getWorkflowById(id: string): Promise<KeyWorkflowItem> {
  return getSingleRecord<KeyWorkflowItem>(baseUrl, id);
}

export function saveWorkflow(
  workflow: KeyWorkflow | AddKeyWorkflowDto
): Promise<KeyWorkflowItem> {
  if (!workflow.maxKeys) {
    workflow.maxKeys = 0;
  }
  if (!workflow.maxValue) {
    workflow.maxValue = 0;
  }
  if (!workflow.maxKeysPerUser) {
    workflow.maxKeysPerUser = 0;
  }

  return saveRecord(baseUrl, workflow);
}

export function deleteWorkflow(id: string): Promise<{ id: string }> {
  return deleteRecord(baseUrl, id);
}

export function getGamesForWorkflows(
  id: string,
  companyId: string
): Promise<GameSkuBase[]> {
  return fetch(`${baseUrl}${id}/games/${companyId || ''}`, apiOptions())
    .then(handleResponse)
    .catch(handleError);
}

export function getStats(id: string): Promise<Stats> {
  return getStatsData(baseUrl, id);
}

export function getStatsByDay(id: string): Promise<unknown> {
  return get(`${baseUrl}${id}/statsByDay/`);
}

export function getAggregateStats(
  id: string,
  from: Date,
  to: Date
): Promise<KeyRequestMetricsAggregates> {
  // Format the from as YYYY-MM-DD
  const fromString = from.toISOString().split('T')[0];
  // Format the to as YYYY-MM-DD
  const toString = to.toISOString().split('T')[0];

  return get(
    `${baseUrl}${id}/aggregateStats?fromDate=${fromString}&toDate=${toString}`
  );
}

export function getAudit(id: string): Promise<AuditItem> {
  return getAuditData(baseUrl, id);
}

export function addAudit(id: string, comment: string): Promise<AuditItem> {
  return postAction(`${baseUrl}${id}/audit`, { comment });
}

export function cloneWorkflow(
  id: string,
  name: string,
  withRestrictions: boolean
): Promise<{ id: string }> {
  return postAction(`${baseUrl}${id}/clone`, { name, withRestrictions });
}

export function saveWorkflowSteps(
  id: string,
  steps: KeyWorkflowStep[]
): Promise<KeyWorkflowStep[]> {
  return putAction(`${baseUrl}${id}/steps`, { steps });
}

export function getWorkflowStepsById(id: string): Promise<KeyWorkflowStep[]> {
  return get(`${baseUrl}${id}/steps`);
}

export function getWorkflowTagsById(id: string): Promise<KeyRequesterTag[]> {
  return get(`${baseUrl}${id}/tags`);
}

export function addWorkflowTag(
  id: string,
  tag: string
): Promise<KeyRequesterTag> {
  return postAction(`${baseUrl}${id}/tag/`, { tag });
}

export function deleteWorkflowTag(
  id: string,
  tag: string
): Promise<KeyRequesterTag> {
  return deleteRecord(`${baseUrl}${id}/tag/`, null, { tag });
}

// ==========================================
// REQUESTERS
export function getWorkflowRequestersById(
  id: string
): Promise<KeyWorkflowRequester[]> {
  return get(`${baseUrl}${id}/requesters`);
}
// by group
export function addWorkflowRequesterGroup(
  id: string,
  userProfileGroupId: string
): Promise<KeyWorkflowRequester> {
  return postAction(
    `${baseUrl}${id}/requester/group/${userProfileGroupId}`,
    null
  );
}

export function deleteWorkflowRequesterGroup(
  id: string,
  userProfileGroupId: string
): Promise<boolean> {
  return deleteRecord(`${baseUrl}${id}/requester/group/`, userProfileGroupId);
}

// by company
export function addWorkflowRequesterCompany(
  id: string,
  companyId: string
): Promise<KeyWorkflowRequester> {
  return postAction(`${baseUrl}${id}/requester/company/${companyId}`, null);
}

export function deleteWorkflowRequesterCompany(
  id: string,
  companyId: string
): Promise<boolean> {
  return deleteRecord(`${baseUrl}${id}/requester/company/`, companyId);
}

// by user
export function addWorkflowRequesterUser(
  id: string,
  userId: string
): Promise<KeyWorkflowRequester> {
  return postAction(`${baseUrl}${id}/requester/user/${userId}`, null);
}

export function deleteWorkflowRequesterUser(
  id: string,
  userId: string
): Promise<boolean> {
  return deleteRecord(`${baseUrl}${id}/requester/user/`, userId);
}

export function exportWorkflowData(params: ApiExportParams): Promise<void> {
  return exportData(
    `${apiUrl}/export/workflows/search`,
    'keyWorkflowName',
    params
  );
}

// ==========================================
// KEY POOLS
export function getKeyPoolBalance(
  keyWorkflowId: string,
  companyId: string,
  gameId: string,
  skuId: string
): Promise<number> {
  return get(
    `${baseUrl}keypoolbalance/${gameId}/${skuId}/${keyWorkflowId}/${
      companyId || ''
    }`
  );
}

export function getApproverNotificationUsers(
  params?: ApiListParams
): Promise<ListApiResponse<ApproverNotificationUser>> {
  return getList<ApproverNotificationUser>(
    `${baseUrl}notification/approver/users`,
    params
  );
}

export function exportApproverNotificationUsers(
  params: ApiExportParams
): Promise<void> {
  return exportData(
    `${apiUrl}/export/workflows/notification/approver/users`,
    'keyWorkflowName',
    params
  );
}

export function sendApproverNotification(
  keyWorkflowStepId: string
): Promise<KeyWorkflowStepApproverNotification> {
  return postAction(
    `${baseUrl}notification/approver/notify/${keyWorkflowStepId}`,
    {}
  );
}

export function addKeyWorkflowStepNotificationFilter(
  keyWorkflowStepId: string,
  userProfileId: string
): Promise<{ id: string }> {
  return postAction(`${baseUrl}notification/approver/userfilter`, {
    keyWorkflowStepId,
    userProfileId,
  });
}

export function deleteKeyWorkflowStepNotificationFilter(
  keyWorkflowStepId: string,
  userProfileId: string
): Promise<{ id: string }> {
  return deleteAction(
    `${baseUrl}notification/approver/userfilter/${keyWorkflowStepId}/${userProfileId}`
  );
}
